import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SEO_LANDINGS_BY_CATEGORY, SEO_LANDINGS } from "@/data/seoLandings";
import SEO, { breadcrumbsLd } from "@/components/site/SEO";
import FinalCTA from "@/components/site/FinalCTA";

const IndustriesPage = () => {
  const categories = Object.keys(SEO_LANDINGS_BY_CATEGORY);

  return (
    <>
      <SEO
        title="Industries We Serve — Professional Services Office Space"
        description={`Office space tailored to ${SEO_LANDINGS.length} professional service industries in Weston, FL — from attorneys and CPAs to consultants, agencies and tech teams. Find the right workspace for your practice.`}
        path="/industries"
        jsonLd={[
          breadcrumbsLd([
            { name: "Home", path: "/" },
            { name: "Industries", path: "/industries" },
          ]),
        ]}
      />

      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="eyebrow">Industries we serve</div>
          <h1 className="hero-display text-[52px] md:text-[96px] mt-5 leading-[1.0] max-w-4xl">
            Built for the way <em className="italic text-[var(--primary)]">your practice</em> actually works.
          </h1>
          <p className="mt-7 max-w-2xl text-[16px] leading-relaxed text-[var(--text-muted)]">
            {SEO_LANDINGS.length} dedicated landing pages, one for each profession we host —
            because the office an attorney needs isn&apos;t the office a marketing agency needs,
            and the building should know the difference.
          </p>
        </div>
      </section>

      {categories.map((cat) => (
        <section key={cat} className="bg-white border-b border-[var(--border)]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <div className="eyebrow">{cat}</div>
                <h2 className="section-title text-[36px] md:text-[52px] mt-3 leading-tight">
                  {cat} & related practices.
                </h2>
              </div>
              <div className="text-[12px] tracking-[0.18em] uppercase text-[var(--text-muted)]">
                {SEO_LANDINGS_BY_CATEGORY[cat].length} dedicated{" "}
                {SEO_LANDINGS_BY_CATEGORY[cat].length === 1 ? "page" : "pages"}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--rule)] hairline">
              {SEO_LANDINGS_BY_CATEGORY[cat].map((l) => (
                <Link
                  key={l.slug}
                  to={`/${l.slug}`}
                  data-testid={`industry-card-${l.slug}`}
                  className="group bg-white p-7 hover:bg-[var(--bg)] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-[22px] leading-tight tracking-tight">
                      {l.h1.replace(/\.$/, "")}
                    </h3>
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.8}
                      className="mt-1 shrink-0 text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors"
                    />
                  </div>
                  <p className="text-[13.5px] text-[var(--text-muted)] mt-3 leading-relaxed line-clamp-3">
                    {l.intro}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      <FinalCTA
        title="Not sure which page fits you?"
        subtitle="Tell us a little about your practice and we'll suggest the right suite within an hour."
      />
    </>
  );
};

export default IndustriesPage;
